export const useAILinkCoder = () => {
  // Haal alle URL's uit de gebruikersinstructie (http(s) en www.-varianten)
  const extractSourceUrls = (text) => {
    if (!text || typeof text !== 'string') return [];

    const urls = new Set();

    // Volledige http(s)-URL's
    const httpRegex = /https?:\/\/[^\s)]+/gi;
    let match;
    while ((match = httpRegex.exec(text)) !== null) {
      urls.add(match[0]);
    }

    // www.-URL's zonder schema
    const wwwRegex = /\bwww\.[^\s)]+/gi;
    while ((match = wwwRegex.exec(text)) !== null) {
      const value = match[0];
      const normalized = value.startsWith('http') ? value : `https://${value}`;
      urls.add(normalized);
    }

    return Array.from(urls);
  };

  const buildPrompt = (userInstructions, fieldConfig) => {
    // Get enabled fields with their configurations
    const enabledFields = Object.entries(fieldConfig.fields)
      .filter(([_, config]) => config.enabled)
      .map(([fieldName, config]) => ({
        name: fieldName,
        displayName: config.displayName,
        type: config.fieldType,
        required: config.required,
        options: config.options || []
      }));

    const textNorm = fieldConfig.textNormalization || {};
    const sourceUrls = extractSourceUrls(userInstructions);

    const urlsSection = sourceUrls.length
      ? `\nHERKENDE BRON-URLS (deze zijn gegeven en mogen NIET worden aangepast of verzonnen):\n${sourceUrls
          .map((u) => `- ${u}`)
          .join('\n')}\n`
      : '';

    const prompt = `Je bent een expert marketing data analist die campagne entries genereert.

GEBRUIKERSINSTRUCTIE:
${userInstructions}
${urlsSection}

BELANGRIJKE REGELS:
- Voor dropdown velden: gebruik ALLEEN de voorgeschreven opties in het schema (nooit de placeholder "Selecteer", "onbekend" of een lege waarde)
- Kies bij dropdowns altijd de optie die inhoudelijk het beste past bij de beschrijving
- Voor het veld "Url": verzin NOOIT zelf een URL. Gebruik alleen de URL's die in de gebruikersinstructie staan. Als er geen URL in de tekst staat, laat "Url" leeg.
- Maak maximaal één entry per unieke URL in de gebruikersinstructie. Als er 3 verschillende URL's in de tekst staan, maak dan maximaal 3 entries; als er maar 1 URL is, maak dan ook maar 1 entry.
- Voor tekstvelden: volg de text normalization regels
  * Lowercase: ${textNorm.lowercase ? 'Ja - alleen kleine letters' : 'Nee'}
  * Spaties vervangen: ${textNorm.replaceSpaces ? 'Ja - vervang door underscore (_)' : 'Nee'}
  * Alleen alfanumeriek: ${textNorm.alphanumericOnly ? 'Ja - alleen letters, cijfers en _' : 'Nee'}
- Voor datumvelden (zoals "Datum"): de applicatie zal zelf de huidige datum invullen; als je toch een datum invult, gebruik dan MM/DD/YYYY format
- Voor het veld "Campagne": vul ALTIJD exact de tekst "onbekend" in (geen andere waarde)
- Alleen de velden "Uiting" en "Linknaam" mogen tussen entries verschillen; alle andere velden moeten binnen één set entries dezelfde waarde hebben

Genereer nu realistische campagne entries gebaseerd op de instructie.`;

    return prompt;
  };

  const buildResponseSchema = (fieldConfig) => {
    const enabledFields = Object.entries(fieldConfig.fields)
      .filter(([_, config]) => config.enabled);

    const properties = {};
    const required = [];

    enabledFields.forEach(([fieldName, config]) => {
      if (config.required) {
        required.push(fieldName);
      }

      if (config.fieldType === 'select' && config.options.length > 0) {
        // Voor dropdown: enum met alle geldige opties (geen placeholder/lege waarden)
        const enumValues = (config.options || [])
          .map(opt => (opt.Id !== undefined ? opt.Id : opt.Name))
          .filter(value => !!value);

        properties[fieldName] = {
          type: 'string',
          description: `${config.displayName}. Kies een waarde uit de gegeven opties (geen "Selecteer", "onbekend" of lege waarde).`,
          enum: enumValues
        };
      } else if (config.fieldType === 'date') {
        properties[fieldName] = {
          type: 'string',
          description: `${config.displayName} in MM/DD/YYYY format`,
          pattern: '^\\d{2}/\\d{2}/\\d{4}$'
        };
      } else {
        // Text field
        properties[fieldName] = {
          type: 'string',
          description: config.displayName
        };
      }
    });

    return {
      type: 'array',
      items: {
        type: 'object',
        properties: properties,
        required: required
      }
    };
  };

  const validateEntries = (entries, fieldConfig) => {
    const errors = [];

    // Check if it's an array
    if (!Array.isArray(entries)) {
      errors.push('Response moet een array zijn');
      return { isValid: false, errors };
    }

    // Check if array has at least one element
    if (entries.length === 0) {
      errors.push('Minimaal 1 entry vereist');
      return { isValid: false, errors };
    }

    // Get enabled fields configuration
    const enabledFields = Object.entries(fieldConfig.fields)
      .filter(([_, config]) => config.enabled)
      .reduce((acc, [name, config]) => {
        acc[name] = config;
        return acc;
      }, {});

    // Validate each entry
    entries.forEach((entry, entryIndex) => {
      if (typeof entry !== 'object' || entry === null) {
        errors.push(`Entry ${entryIndex + 1}: moet een object zijn`);
        return;
      }

      // Check required fields
      Object.entries(enabledFields).forEach(([fieldName, fieldConfig]) => {
        if (fieldConfig.required) {
          if (!entry[fieldName] || entry[fieldName] === '') {
            errors.push(`Entry ${entryIndex + 1}: verplicht veld '${fieldName}' ontbreekt`);
          }
        }

        // Validate date format
        if (entry[fieldName] && fieldConfig.fieldType === 'date') {
          const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
          if (!dateRegex.test(entry[fieldName])) {
            errors.push(`Entry ${entryIndex + 1}: '${fieldName}' moet MM/DD/YYYY format zijn, kreeg '${entry[fieldName]}'`);
          }
        }
      });

      // Check for unknown fields
      Object.keys(entry).forEach(key => {
        if (!enabledFields[key]) {
          errors.push(`Entry ${entryIndex + 1}: onbekend veld '${key}'`);
        }
      });
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // Pas bedrijfsregels toe die niet (alleen) via AI bepaald moeten worden
  const applyBusinessRules = (entries, fieldConfig, sourceUrls = []) => {
    if (!Array.isArray(entries) || entries.length === 0) {
      return entries;
    }

    // Enabled fields map
    const enabledFields = Object.entries(fieldConfig.fields)
      .filter(([_, config]) => config.enabled)
      .reduce((acc, [name, config]) => {
        acc[name] = config;
        return acc;
      }, {});

    const hasDatum = !!enabledFields.Datum;
    const hasCampagne = !!enabledFields.Campagne;
    const hasUiting = !!enabledFields.Uiting;
    const hasLinknaam = !!enabledFields.Linknaam;
    const hasUrl = !!enabledFields.Url;

    // 1) Datum: huidige datum in MM/DD/YYYY format voor alle entries
    let todayStr = null;
    if (hasDatum) {
      const today = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      todayStr = `${pad(today.getMonth() + 1)}/${pad(today.getDate())}/${today.getFullYear()}`;
    }

    entries.forEach((entry) => {
      if (hasDatum) {
        entry.Datum = todayStr;
      }
      if (hasCampagne) {
        // 2) Campagne altijd "onbekend"
        entry.Campagne = 'onbekend';
      }
    });

    // 3) Alle andere velden gelijk houden binnen de set, behalve Uiting en Linknaam
    const sharedFieldNames = Object.keys(enabledFields).filter(
      (name) => name !== 'Uiting' && name !== 'Linknaam'
    );

    const firstEntry = entries[0] || {};
    sharedFieldNames.forEach((fieldName) => {
      const sharedValue = firstEntry[fieldName] ?? '';
      entries.forEach((entry) => {
        entry[fieldName] = sharedValue;
      });
    });

    // 4) Voor alle dropdownvelden (behalve Uiting): zorg voor een geldige, niet-lege optie
    Object.entries(enabledFields).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.fieldType === 'select' && fieldConfig.options.length > 0 && fieldName !== 'Uiting') {
        const validOptions = (fieldConfig.options || [])
          .map(opt => (opt.Id !== undefined ? opt.Id : opt.Name))
          .filter(value => !!value);

        if (validOptions.length === 0) {
          return;
        }

        const current = entries[0][fieldName];
        const chosen = validOptions.includes(current) ? current : validOptions[0];

        entries.forEach((entry) => {
          entry[fieldName] = chosen;
        });
      }
    });

    // 5) Uiting: gebruik AI-keuzes als ze geldig zijn; alleen ongeldig/lege waarden corrigeren
    if (hasUiting) {
      const uitingConfig = enabledFields.Uiting;
      const optionValues = (uitingConfig.options || [])
        .map(opt => (opt.Id !== undefined ? opt.Id : opt.Name))
        .filter(value => !!value); // leeg/placeholder eruit
      if (optionValues.length > 0) {
        // Zoek eerst een geldige waarde die AI al gekozen heeft
        let sharedUiting = null;
        for (const entry of entries) {
          if (optionValues.includes(entry.Uiting)) {
            sharedUiting = entry.Uiting;
            break;
          }
        }

        // Als AI niets bruikbaars gaf, kies de eerste geldige optie als default
        if (!sharedUiting) {
          sharedUiting = optionValues[0];
        }

        entries.forEach((entry) => {
          const current = entry.Uiting || '';
          if (!optionValues.includes(current)) {
            // Alleen ongeldige/lege waarden corrigeren
            entry.Uiting = sharedUiting;
          }
          // Als AI een geldige waarde had (ook als die per entry verschilt), laten we die staan
        });
      }
    }

    // 6) Linknaam: per entry een unieke waarde
    if (hasLinknaam) {
      const nameCounts = {};

      entries.forEach((entry, index) => {
        let base = (entry.Linknaam || '').trim();
        if (!base) {
          base = `uiting_${index + 1}`;
        }

        if (!nameCounts[base]) {
          nameCounts[base] = 1;
          entry.Linknaam = base;
        } else {
          nameCounts[base] += 1;
          entry.Linknaam = `${base}_${nameCounts[base]}`;
        }
      });
    }

    // 7) Url: gebruik bron-URL's uit de instructietekst als die bekend zijn
    if (hasUrl && Array.isArray(sourceUrls) && sourceUrls.length > 0) {
      entries.forEach((entry, index) => {
        const url = sourceUrls[Math.min(index, sourceUrls.length - 1)];
        entry.Url = url;
      });
    }

    // 8) Maximaal 1 entry per unieke Url
    if (hasUrl) {
      const seen = new Set();
      const uniqueEntries = [];

      entries.forEach((entry) => {
        const url = entry.Url;
        if (!url) {
          // Als er geen URL is, laten we maximaal 1 zo'n entry toe
          const key = '__no_url__';
          if (!seen.has(key)) {
            seen.add(key);
            uniqueEntries.push(entry);
          }
          return;
        }

        if (seen.has(url)) {
          return; // dubbele codering voor dezelfde link laten we vallen
        }

        seen.add(url);
        uniqueEntries.push(entry);
      });

      entries = uniqueEntries;
    }

    return entries;
  };

  const generateEntries = async (userInstructions, fieldConfig) => {
    try {
      // Build the prompt and schema
      const prompt = buildPrompt(userInstructions, fieldConfig);
      const sourceUrls = extractSourceUrls(userInstructions);
      const responseSchema = buildResponseSchema(fieldConfig);

      // Call backend API that wraps ChatGPT
      const res = await fetch('/api/aiGenerateEntries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt,
          responseSchema
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const backendError = data && data.error ? data.error : `HTTP fout ${res.status}`;
        return {
          success: false,
          entries: null,
          errors: [backendError]
        };
      }

      const data = await res.json();

      if (!data.success) {
        return {
          success: false,
          entries: null,
          errors: [data.error || 'Onbekende AI-fout op de backend.']
        };
      }

      let entries;
      try {
        entries = JSON.parse(data.json);
      } catch (parseError) {
        return {
          success: false,
          entries: null,
          errors: ['AI response bevat geen valide JSON. Probeer de instructie opnieuw te formuleren.']
        };
      }

      // Pas domeinregels toe (datum, campagne, variatie in velden, URL, etc.)
      entries = applyBusinessRules(entries, fieldConfig, sourceUrls);

      // Validate the entries na toepassing van de regels
      const validation = validateEntries(entries, fieldConfig);

      if (!validation.isValid) {
        return {
          success: false,
          entries: entries,
          errors: validation.errors
        };
      }

      // Success!
      return {
        success: true,
        entries: entries,
        errors: []
      };
    } catch (error) {
      console.error('Error generating entries:', error);

      if (error.message?.includes('network') || error.message?.includes('fetch')) {
        return {
          success: false,
          entries: null,
          errors: ['Netwerkfout. Controleer je internetverbinding en probeer opnieuw.']
        };
      }

      return {
        success: false,
        entries: null,
        errors: [`Fout bij AI generatie: ${error.message}`]
      };
    }
  };

  return {
    generateEntries
  };
};

export default useAILinkCoder;
